import React from 'react';
import { CalendarDays, CalendarPlus, Columns3, ListTree } from 'lucide-react';
import { EVENT_TYPES } from '../../utils/ipoCalendar';
import { TYPE_STYLES } from './calendarStyles';

export const CALENDAR_VIEWS = [
  { key: 'timeline', label: 'Timeline Schedule', icon: ListTree },
  { key: 'grid', label: 'Monthly Grid', icon: CalendarDays },
  { key: 'pipeline', label: 'Pipeline Flow', icon: Columns3 },
];

const FILTER_TYPES = ['open', 'close', 'allotment', 'holiday'];

export const CalendarToolbar = ({
  view,
  onViewChange,
  typeCounts,
  totalCount,
  activeTypes,
  onToggleType,
  onClearTypes,
  board,
  onBoardChange,
  boardCounts,
  onSyncAll,
  canSync,
}) => (
  <div className="flex flex-col xl:flex-row xl:items-center gap-3 bg-white rounded-2xl border border-slate-200/90 shadow-sm p-2.5 sm:p-3">
    {/* View switcher */}
    <div role="group" aria-label="Calendar view" className="inline-flex self-start p-1 rounded-xl bg-slate-100 border border-slate-200/80 overflow-x-auto max-w-full">
      {CALENDAR_VIEWS.map(({ key, label, icon: Icon }) => {
        const active = view === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={() => onViewChange(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
              active ? 'bg-white text-[#008080] shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>

    {/* Event type filters */}
    <div role="group" aria-label="Filter events" className="flex flex-wrap items-center gap-1.5 xl:flex-1">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1">Events</span>
      <button
        type="button"
        aria-pressed={activeTypes.length === 0}
        onClick={onClearTypes}
        className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
          activeTypes.length === 0
            ? 'bg-slate-900 border-slate-900 text-white'
            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
        }`}
      >
        All ({totalCount})
      </button>
      {FILTER_TYPES.map((type) => {
        const active = activeTypes.includes(type);
        return (
          <button
            key={type}
            type="button"
            aria-pressed={active}
            onClick={() => onToggleType(type)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
              active ? TYPE_STYLES[type].badge : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${TYPE_STYLES[type].dot}`} />
            {type === 'holiday' ? 'Holidays' : EVENT_TYPES[type].label} ({typeCounts[type] || 0})
          </button>
        );
      })}
    </div>

    {/* Board + export */}
    <div className="flex items-center gap-2">
      <label htmlFor="calendar-board" className="sr-only">
        Board
      </label>
      <select
        id="calendar-board"
        value={board}
        onChange={(e) => onBoardChange(e.target.value)}
        className="flex-1 xl:flex-none px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
      >
        <option value="all">All boards ({boardCounts.all})</option>
        <option value="mainboard">Mainboard IPOs ({boardCounts.mainboard})</option>
        <option value="sme">SME IPOs ({boardCounts.sme})</option>
      </select>
      <button
        type="button"
        onClick={onSyncAll}
        disabled={!canSync}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#008080] hover:bg-[#006666] text-white text-xs font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <CalendarPlus className="w-3.5 h-3.5" />
        Sync All
      </button>
    </div>
  </div>
);

export default CalendarToolbar;
